import { useEffect, useRef } from "react";
import { FormControl, Button, InputLabel } from '@mui/material';

import { MySelect, MyMenuItem } from "~/mui/MySelect";
import MyDialog from "~/mui/MyDialog";
import { getServiceName, removeFromArray } from "~/utils/utils";
import { useTrans } from "~/hooks/useTrans";
import { IService } from "~/providers/interfaces";
import MyList, { IMyListItem } from "~/mui/MyList";
import { apiGetServices, apiUpdateProjectLinkService, apiUpdateProjectUnlinkService } from "~/providers/api";
import { IUrlParams } from "~/interfaces";
import Loader from '~/components/Loader';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";

import "./style.less";

interface IState {
	loading: boolean;
	servicesOrig: Array<IService>;
	addServices: Array<IService>;
	serviceValue: string;
	items: Array<IMyListItem>;
	dialogLoading: boolean;
	errorMsg: string;
}

interface IEditProjectServicesModal {
	services: Array<IService>;
	params: IUrlParams;
	lastUpdate: string;
	onClose?: () => void;
	onSave?: (services: Array<IService>, lastUpdate: string) => void;
	onChange?: (isChanged: boolean) => void;
}

export default function EditProjectServicesModal({
	services = [],
	params = null,
	lastUpdate = "",
	onClose = () => {},
	onSave = () => {},
	onChange = () => {},
}: IEditProjectServicesModal) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		loading: true,
		servicesOrig: [],
		addServices: [],
		serviceValue: "",
		items: services.map(service => ({ id: service.id, title: getServiceName(service) })),
		dialogLoading: false,
		errorMsg: "",
	});
	const lastUpdateRefData = useRef<string>(lastUpdate);

	function getFilteredItems() {
		const linkItems: Array<number> = [];
		const unlinkItems: Array<number> = [];

		for (const item of state.items) {
			const ind = services.findIndex(serviceItem => serviceItem.id === item.id);

			// neni zmena
			if (ind !== -1) {
				continue;
			}

			// jinak pridame
			linkItems.push(item.id);
		}

		// puvodni data
		for (const item of services) {
			const ind = state.items.findIndex(stateItem => stateItem.id === item.id);

			// smazano
			if (ind === -1) {
				unlinkItems.push(item.id);
			}
		}

		return {
			linkItems,
			unlinkItems,
		};
	}

	function getWasChanged() {
		const filteredData = getFilteredItems();

		return filteredData.linkItems.length > 0 || filteredData.unlinkItems.length > 0;
	}

	function onListRemove(item: IMyListItem) {
		const items = removeFromArray(state.items, itemData => itemData.id === item.id);
		const curIndexes = items.map(curItem => curItem.id);
		const addServices = state.servicesOrig.filter(service => curIndexes.indexOf(service.id) === -1);

		updateState({
			items,
			serviceValue: addServices.length ? addServices[0].id.toString() : "",
			addServices,
		});
		onChange(true);
	}

	async function dialogOnSave() {
		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const filteredData = getFilteredItems();
		let curServices: Array<IService> = [];

		/* eslint-disable no-await-in-loop */
		/* eslint-disable require-atomic-updates */
		if (filteredData.linkItems.length > 0) {
			for (const linkItem of filteredData.linkItems) {
				const linkData = await apiUpdateProjectLinkService(params.projectId, linkItem, lastUpdateRefData.current, lang);

				if (!linkData.noErrors) {
					updateState({
						dialogLoading: false,
						errorMsg: linkData.errors.join(", "),
					});
					break;
				}

				lastUpdateRefData.current = linkData.lastUpdate;
				curServices = linkData.data;
			}
		}

		if (filteredData.unlinkItems.length > 0) {
			for (const unlinkItem of filteredData.unlinkItems) {
				const unlinkData = await apiUpdateProjectUnlinkService(params.projectId, unlinkItem, lastUpdateRefData.current, lang);

				if (!unlinkData.noErrors) {
					updateState({
						dialogLoading: false,
						errorMsg: unlinkData.errors.join(", "),
					});
					break;
				}

				lastUpdateRefData.current = unlinkData.lastUpdate;
				curServices = unlinkData.data;
			}
		}

		onSave(curServices, lastUpdateRefData.current);
	}

	function addService() {
		const serviceData = state.servicesOrig.filter(service => service.id.toString() === state.serviceValue)[0];

		if (serviceData) {
			const newItems = [
				...state.items,
				{
					id: serviceData.id,
					title: getServiceName(serviceData),
				},
			];
			const curIndexes = newItems.map(item => item.id);
			const addServices = state.servicesOrig.filter(item => curIndexes.indexOf(item.id) === -1);

			updateState({
				items: newItems,
				serviceValue: addServices.length ? addServices[0].id.toString() : "",
				addServices,
			});
			onChange(true);
		}
	}

	async function loadData() {
		updateState({
			loading: true,
			errorMsg: "",
		});

		const allServicesData = await apiGetServices(lang);
		const curIndexes = services.map(item => item.id);
		const addServices = allServicesData.data.filter(item => curIndexes.indexOf(item.id) === -1);

		updateState({
			loading: false,
			...allServicesData.noErrors
				? {
					servicesOrig: allServicesData.data,
					serviceValue: addServices.length ? addServices[0].id.toString() : "",
					addServices,
				}
				: {},
			errorMsg: allServicesData.errors.join(", "),
		});
	}

	useEffect(() => {
		loadData();
	}, []);

	return <MyDialog title={trans("editProjectServicesModal.title")} onClose={onClose} onSave={dialogOnSave} dialogLoading={state.dialogLoading} errorMsg={state.errorMsg}
		disableSave={!getWasChanged()} confirmClose={getWasChanged()} cancelCaption={ trans("cancel") } className="editProjectServicesModal" contentClassName="editProjectServicesModal__content">
		{ state.loading && <Loader msg={trans("loadingData")} /> }
		{ state.addServices.length > 0 && <div className="editProjectServicesModal__controls">
			<FormControl size="small" sx={{ flex: "100%" }}>
				<InputLabel>{ trans("menu.adminServices") }</InputLabel>
				<MySelect
					value={state.serviceValue}
					onChange={event => updateState({ serviceValue: event.target.value as string })}
					label={ trans("menu.adminServices") }
				>
					{ state.addServices.map(service => <MyMenuItem value={service.id.toString()} key={service.id}>
						{ getServiceName(service) }
					</MyMenuItem>) }
				</MySelect>
			</FormControl>
			<Button className="editProjectServicesModal__addBtn" color="success" variant="contained" onClick={addService}>
				{ trans("add") }
			</Button>
		</div> }
		<MyList items={state.items} onRemove={onListRemove} sx={{ flexDirection: "column" }} />
	</MyDialog>;
}
